import 'allsettled-polyfill';
import { initialize as initializeFeed } from '@finn-no/feed';
import { writeExperimentData } from '@finn-no/unleash-cookie/unleash-cookie.js';

import { debouncedLoadTags } from './podlet/ast';
import { HorseshoeConfig } from '../shared/types/podlet';
import { getPodletConfigFromHtml, pageUsesRecomPodlet } from './podlet/config';
import {
    verticalSpecificContentMarketing,
    verticalSpesificInit,
} from './podlet/vertical';
import { Apntag, BannerGlobal, Finn } from '../shared/types/ast';
import { requestBanners } from './utils/banners';
import { renderHorseshoe } from './components/horseshoe/horseshoe-render';
import { renderSkyscraper } from './components/skyscraper/skyscraper-render';

import { version } from '../shared/version';
import diagnostics from './utils/diagnostics';
import {
    markHorseshoeInitiated,
    markHorseshoeLoaded,
} from './utils/performance';

import { renderBoard } from './components/board/board-render';
import { renderContentBoard } from './components/content-board/content-board-render';
import { loadGlimrIfPermission } from './utils/glimr';
import { copyQueue } from './stories/helper';
import { persistPPID } from './utils/pulse-ppid';
import { definedNativeAd } from './components/web-components/native-ad/native-ad.component';
import { getRefreshState } from './refresh-state';
import { defineRecircNativeAd } from './components/web-components/recirc-native-ad/recirc-native-ad.component';
import { displayAdsLog } from './utils/debug';
import { subscribe } from './podlet/events';
import { setGoogleReqData } from './components/web-components/recirc-google-ad/recirc-google-ad.react';

const refreshState = getRefreshState();

window.FINN = window.FINN || ({} as Finn);
window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
window.FINN.banner.queue = window.FINN.banner.queue || [];
window.FINN.banner.debouncedLoadTags =
    window.FINN.banner.debouncedLoadTags || debouncedLoadTags();

window.apntag = window.apntag || ({} as Apntag);
window.apntag.anq = window.apntag.anq || [];

async function loadConfig(config: HorseshoeConfig) {
    refreshState.clientReady = !config.waitForClient;

    // Check if object is not empty
    if (config?.experiments && Object.keys(config?.experiments).length !== 0) {
        writeExperimentData(config.experiments);
    }

    // persist PPID data
    // NB! A new await here might be a terrible idea
    await Promise.all([
        await persistPPID(),
        await loadGlimrIfPermission(config),
    ]);

    const { xandr } = config;

    refreshState.setInitialData(config);

    if (xandr && xandr.pageOpts) {
        if (window.apntag) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    displayAdsLog('index.ts: request banners');
                    requestBanners(
                        window.apntag,
                        refreshState.getXandrRequestData(),
                        true,
                        true,
                    );
                }
                if (refreshState.clientReady) {
                    displayAdsLog('index.ts: client ready > copy queue');
                    copyQueue();
                }
            });
        }

        refreshState.setupSubscriptions(config);

        initializeFeed();

        const props = {
            deviceType: config.deviceType,
            vertical: config.vertical,
            pageType: config.pageType,
        };

        if (!config.configOnly) {
            if (config.skyscraperOnly) {
                displayAdsLog(`index.ts render skyscraperOnly`);
                renderSkyscraper('horseshoe-root', props);
            } else {
                displayAdsLog(`index.ts render horseshoe`);
                renderHorseshoe('horseshoe-root', props);
            }
        }
        if (config.pageType === 'object') {
            renderContentBoard('contentboard', {
                ...props,
                id: 'contentboard',
            });
        }

        if (config.deviceType === 'mobile' && config.pageType === 'object') {
            renderBoard('board_1', props);
        }

        verticalSpesificInit(config);
        verticalSpecificContentMarketing(config);
    }
}

function init() {
    markHorseshoeInitiated();
    const config = getPodletConfigFromHtml();

    if (config.layoutHasNoTroika) {
        definedNativeAd();
    }
    defineRecircNativeAd();

    if (pageUsesRecomPodlet(config)) {
        subscribe('recirc-google-ad', 'init', setGoogleReqData);
    }

    if (config.version === version) {
        diagnostics.groupCollapsed(
            'displayads-podlet-horseshoe loading version: %s ',
            version,
        );
        diagnostics(config);
        diagnostics.groupEnd();
    } else {
        diagnostics(
            `Server version (${config.version}) does not match client version (${version})`,
        );
    }

    loadConfig(config);
}

markHorseshoeLoaded();
document.addEventListener('DOMContentLoaded', init);
